import React, { useMemo, useContext, Fragment } from 'react';
import { Skeleton } from '@material-ui/lab';
import { Tooltip, Fab } from '@material-ui/core';
import { AddRounded as AddRoundedIcon } from '@material-ui/icons';
import {
  ADD_PRODUCT_INFO, IPRODUCT_COLLECTION, PRODUCT_TO_BE_ADDED, TRUE,
  CONFIRMATION_POPUP_KEY, RESPONSE_POPUP_KEY, MODAL_KEY, DIMENSION,
  ALLOW_ADD_PRODUCT_TO_ORDER_APP_SETTING,
} from '../../../constants/patientChart.constant';
import { PatientChartContext } from '../../../contexts/patientChart';
import PPButton, { VARIENT as ButtonVarient } from '../../subComponents/button';
import Input from '../../subComponents/input';
import Select from '../../subComponents/select';
import Modal from '../../subComponents/commonModal/v3';
import { POPUP_TYPE } from '../../subComponents/popup';
import { getLoggedInUser } from '../../../utils/jose';

export const AddProduct = ({
  data: {
    productCost = {},
    productInfo = {},
    addProductInfo = ADD_PRODUCT_INFO(),
    changeProducts = {},
    version = 2,
  },
  action: {
    getProductCost = async () => {},
    resetProductCost = async () => {},
    addProduct = async () => {},
  },
}) => {
  const { ModalCollection, handleModalOpen, handleMultipleModalClose, productToBeAdded, setProductToBeAdded, handleLoadProductsListing } = useContext(PatientChartContext);

  const doctorId = useMemo(() => getLoggedInUser().UserId, []);

  const { products, productCollection } = useMemo(() => {
    const productCollection = new Map([[-1, IPRODUCT_COLLECTION()]]);

    if (!changeProducts.api.data.size) {
      return { products: [], productCollection };
    }

    const collection = new Map(changeProducts.api.data);
    const productMap = new Map(productInfo.productMap);
    /**
     * @description filters the unique products along with all the required fields from reducer's cache.
     */
    [...collection.entries()].forEach(([targetId = 0, items = []]) => {
      const filteredItems = items.filter(({ Value }) => !collection.has(Value));
      filteredItems.forEach(({ Title, PrescriberProductDisplayName, Value: id }) => {
        const title = PrescriberProductDisplayName || Title;
        const targetProduct = productMap.get(targetId) || { AutRefill: false, Pharmacy: '', Shipping: '', Qty: 0 };
        const _product = {
          id,
          value: id,
          title,
          label: title,
          summary: {
            autoRefill: targetProduct.AutRefill,
            pharmacy: targetProduct.Pharmacy,
            shipper: targetProduct.Shipping,
            quantity: targetProduct.Qty,
          }
        };
        productCollection.set(_product.id, _product);
      });
    });

    const _products = [...productCollection.values()].filter(({ id }) => id !== 0);
    return { products: _products, productCollection };
  }, [productInfo, changeProducts]);

  const { existingProductNamesConcatenated } = useMemo(() => {
    const existingProductNames = [];
    let existingProductNamesConcatenated = '';
    for (let i = 0; i < productInfo.Product.length; i++) {
      const _product = productInfo.Product[i];
      existingProductNames.push(_product.displayTitle);

      if (existingProductNamesConcatenated.length)
        existingProductNamesConcatenated += ', ';
      existingProductNamesConcatenated += _product.displayTitle;
    }
    return { existingProductNames, existingProductNamesConcatenated };
  }, [productInfo]);

  const isAddProductEnabled = useMemo(() => {
    const applicationSetting = (() => {
      try {
        return new Map(productInfo.StoreFrontData.StorefrontApplicationSettings) || new Map();
      } catch (e) {
        return new Map();
      }
    })();

    if (!applicationSetting.has(ALLOW_ADD_PRODUCT_TO_ORDER_APP_SETTING)) {
      return false;
    }

    try {
      const config = applicationSetting.get(ALLOW_ADD_PRODUCT_TO_ORDER_APP_SETTING);
      return config.value.trim().toLowerCase() === TRUE;
    } catch (e) {
      // error handling is needed
    }

    return false;
  }, [productInfo]);

  const { _productCost, _totalOrderCost } = useMemo(() => {
    const { ProductCost, TotalOrderCost } = productCost.api.data;
    const _productCost = ProductCost * productToBeAdded.summary.quantity;
    const _totalOrderCost = TotalOrderCost + _productCost;
    return {
      _productCost,
      _totalOrderCost,
    };
  }, [productCost.api.data, productToBeAdded.summary.quantity]);

  const handleChange = async ({ target }) => {
    const productId = Number(target.value);
    const product = productCollection.get(productId);

    setProductToBeAdded({
      ...productToBeAdded,
      value: target.value,
      summary: {
        productId: product.id,
        product: product.label,
        pharmacy: product.summary.pharmacy,
        shipper: product.summary.shipper,
        quantity: product.summary.quantity,
      },
    });

    if (Number(target.value) === -1) {
      resetProductCost();
      return {};
    }

    try {
      const body = {
        orderId: productInfo.OrderId,
        productId,
        storefrontCompanyId: productInfo.StoreFrontData.CompanyId,
      };
      await getProductCost(body);
    } catch (e) {
      // 
    }

    return {};
  };

  const SkeletonLoader = <Skeleton variant='text' className='flex width-100' />;

  const ProductCostComponent = new Map([
    ['false-false', <Fragment />],
    ['false-true', SkeletonLoader],
    ['true-false', `$${_productCost}`],
    ['true-true', SkeletonLoader],
  ]);

  const ProductShippingCostComponent = new Map([
    ['false-false', <Fragment />],
    ['false-true', SkeletonLoader],
    ['true-false', `$${productCost.api.data.ProductShippingCost}`],
    ['true-true', SkeletonLoader],
  ]);

  const TotalOrderCost = new Map([
    ['false-false', <Fragment />],
    ['false-true', SkeletonLoader],
    ['true-false', `$${_totalOrderCost}`],
    ['true-true', SkeletonLoader],
  ]);

  const handleAddProduct = async ({ keys }) => {
    handleMultipleModalClose({ keys: [CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT] });

    try {
      const body = {
        orderId: productInfo.OrderId,
        productId: productToBeAdded.summary.productId,
        doctorId: doctorId,
        productCost: productCost.api.data.ProductCost,
        totalOrderCost: _totalOrderCost,
        qty: productToBeAdded.summary.quantity,
        storefrontCompanyId: productInfo.StoreFrontData.CompanyId,
      };

      const { status, data } = await addProduct(body);
      if (status === 200) {
        handleMultipleModalClose.call(this, { keys });
        setProductToBeAdded(PRODUCT_TO_BE_ADDED());
        resetProductCost();
        handleLoadProductsListing({ OrderId: productInfo.OrderId });
      }

      if (status !== 200) {
        const { Error: error = 'Failed to add product. Please try later.' } = data;
        const heading = 'Error';

        handleModalOpen({
          key:
          RESPONSE_POPUP_KEY.ADD_NEW_PRODUCT, modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch (e) {
      // 
    }
  };

  const Template = (
    <div className='flex flex-column flex-fill m-b-5'>
      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="input-label font-roboto m-b-2">Add New Product:</label>
        <Select
          id='productId'
          labelId="productId"
          value={productToBeAdded.value}
          onChange={handleChange}
          items={products}
        />
      </div>

      <div className='flex flex-column justify-content-center m-b-5 d-none'>
        <label className="input-label font-roboto m-b-2">Pharmacy:</label>
        <Input
          name='pharmacy'
          type='text'
          label=''
          className='flex flex-fill m-b-2'
          labelClassName=''
          value={productToBeAdded.summary.pharmacy}
          error={false}
          message={''}
          onChange={() => {}}
        />
        <div className='flex font-roboto font-14'>{productToBeAdded.summary.pharmacy}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5 d-none'>
        <label className="input-label font-roboto m-b-2">Select Shipping Services:</label>
        <Input
          name='pharmacy'
          type='text'
          label=''
          className='flex flex-fill m-b-2'
          labelClassName=''
          value={productToBeAdded.summary.shipper}
          error={false}
          message={''}
          onChange={() => {}}
        />
        <div className='flex font-roboto font-14'>{productToBeAdded.summary.shipper}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-2'>
        <label className="input-label font-roboto m-b-2">Summary</label>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Product:</label>
        <div className='flex font-roboto font-14 bold'>{productToBeAdded.summary.product}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Quantity:</label>
        <div className='flex font-roboto font-14 bold'>{productToBeAdded.summary.quantity}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Pharmacy:</label>
        <div className='flex font-roboto font-14 bold'>{productToBeAdded.summary.pharmacy}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Shipper:</label>
        <div className='flex font-roboto font-14 bold'>{productToBeAdded.summary.shipper}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Product Cost:</label>
        <div className='flex font-roboto font-14 bold'>{ProductCostComponent.get(`${productCost.api.called}-${productCost.api.loading}`)}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Shipping Cost:</label>
        <div className='flex font-roboto font-14 bold'>{ProductShippingCostComponent.get(`${productCost.api.called}-${productCost.api.loading}`)}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Total Cost:</label>
        <div className='flex font-roboto font-14 bold'>{TotalOrderCost.get(`${productCost.api.called}-${productCost.api.loading}`)}</div>
      </div>

      <div className='flex flex-column font-roboto font-14 bold'>
        Please inform the patient about total order cost of the prescription that will be charged upon approval.
      </div>
    </div>
  );

  const Actions = (
    <div className='flex justify-content-flex-end'>
      <PPButton label='ADD NEW PRODUCT' variant='contained' loading={addProductInfo.api.loading} disabled={productToBeAdded.value === -1} endGutter onClick={handleModalOpen.bind(this, { key: CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT })} />
      <PPButton label='CANCEL' variant='cancel' onClick={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.ADD_NEW_PRODUCT] })} />
    </div>
  );

  const AddProductButtonCollection = new Map([
    ['true-true-1', (
      <PPButton
        variant={ButtonVarient.outlined}
        startIcon={<AddRoundedIcon fontSize='small' className='primary-button' />}
        label='Add Product'
        classes={{ outlinedButtonStyle: "p-l-2-i p-r-2-i" }}
        onClick={handleModalOpen.bind(this, { key: MODAL_KEY.ADD_NEW_PRODUCT, modal: {} })}
      />
    )],
    ['true-false-1', <Fragment />],
    ['false-true-1', <Fragment />],
    ['false-false-1', <Fragment />],
    ['true-true-2', (
      <Tooltip title='ADD PRODUCT' placement='top' arrow>
        <Fab size='small' aria-label='add' className='absolute-important add-product-button' onClick={handleModalOpen.bind(this, { key: MODAL_KEY.ADD_NEW_PRODUCT, modal: {} })}>
          <AddRoundedIcon fontSize='small' />
        </Fab>
      </Tooltip>
    )],
    ['true-false-2', <Fragment />],
    ['false-true-2', <Fragment />],
    ['false-false-2', <Fragment />],
  ]);

  return (
    <Fragment>
      {AddProductButtonCollection.get(`${isAddProductEnabled}-${products.length > 0}-${version}`)}

      <Modal
        title='ADD NEW PRODUCT'
        open={ModalCollection.has(MODAL_KEY.ADD_NEW_PRODUCT)}
        style={DIMENSION.REGULAR2}

        saveConfirmation={ModalCollection.has(CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT)}
        saveConfirmationHeading='Confirmation'
        saveConfirmationMessage={`Confirm you are adding product ${productToBeAdded.summary.product} to this order along with the current product(s) ${existingProductNamesConcatenated}.`}
        onSaveConfirmationYes={handleAddProduct.bind(this, { keys: [MODAL_KEY.ADD_NEW_PRODUCT, CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT] })}
        onSaveConfirmationNo={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.ADD_NEW_PRODUCT, CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT] })}
      >
        <div className='flex flex-column flex-fill m-5 patient-chart-container'>
          {Template}
          {Actions}
        </div>
      </Modal>
    </Fragment>
  );
};

export default AddProduct;
